import {
  basicCons,
  basicPros,
  getRandomProsConsPhrases,
  specLabel,
  weightCons,
  weightPros,
} from './utils'
import { pipe, propOr, toLower } from 'rambda'
import { byAsc } from '@utils/collections'
import { decimal } from '@utils/filters'

export default (articleLaptop, laptopEvaluations, laptopIdsSelected, articleTopic) => {
  const laptop = laptopEvaluations
    .find(evaluation => evaluation.id === articleLaptop.laptopId)

  const articleLaptopsContext = {
    laptop,
    laptopEvaluations,
    articleLaptop,
    laptopIdsSelected,
    articleTopic,
  }

  const pros = getRandomProsConsPhrases(allPros, articleLaptopsContext)
  const cons = getRandomProsConsPhrases(allCons, articleLaptopsContext)

  return { pros, cons }
}

const allPros = [
  basicPros('cpuCinebench', specLabel('cpu', 'processor')),
  basicPros('gpuMark11', specLabel('gpu', 'graphics card')),
  basicPros('screenScore', specLabel('screen', 'display')),
  basicPros('memoryAmount', specLabel('memoryAmount', 'memory amount', 'GB')),
  weightPros,

  ({ laptop, laptopEvaluations }) => {
    if (laptop.price < 500 && laptop.batteryScore >= 5) {
      return [
        'Good battery life for a cheap laptop',
      ]
    }

    return basicPros('batteryMinutes', specLabel('batteryMinutes', 'battery life', 'min'))({
      laptop,
      laptopEvaluations,
    })
  },

  ...(() => {
    const processors = [
      { key: 'i9', label: 'Intel i9' },
      { key: 'i7', label: 'Intel i7' },
      { key: 'i5', label: 'Intel i5' },
      { key: 'ryzen 9', label: 'AMD Ryzen 9' },
      { key: 'ryzen 7', label: 'AMD Ryzen 7' },
      { key: 'ryzen 5', label: 'AMD Ryzen 5' },
    ]

    return processors.map(({ key, label }) => ({ laptopEvaluations, articleLaptop }) => {
      const [cheapestLaptop] = laptopEvaluations
        .filter(({ cpu }) => cpu && cpu.toLowerCase().includes(key))
        .sort(byAsc('price'))

      if (!cheapestLaptop) return []

      if (cheapestLaptop.id === articleLaptop.laptopId) {
        return [
          `One of most affordable laptops with an ${label} processor`,
        ]
      }
    })
  })(),

  ({ laptop }) => {
    const tbs = Number(decimal(laptop.ssdAmount / 1000))
    const tbsSuffix = adverb => `${adverb} ${tbs}TB SSD`

    if (laptop.ssdAmount >= 2000) {
      return [
        'Colossal',
        'Enormous',
        'Gigantic',
        'Herculean',
        'Massive',
        'Monumental',
        'Stupendous',
      ].map(tbsSuffix)
    }

    if (laptop.ssdAmount >= 1000) {
      return [
        'Ample',
        'Fat',
        'Hefty',
        'Large',
        'Mighty',
        'Sizable',
        'Spacious',
      ].map(tbsSuffix)
    }

    const ssdAmount = laptop.ssdAmount ? `${laptop.ssdAmount} GB SSD` : null
    const hddAmount = laptop.hddAmount ? `${laptop.hddAmount} GB HDD` : null
    const storage = (ssdAmount || hddAmount)
      ? `${[ssdAmount, hddAmount].filter(Boolean).join(' + ')}`
      : ''

    if (laptop.storageScore > 5 && laptop.storageScore < 7) {
      return [
        `A lot of storage ${storage}`,
      ]
    }

    if (laptop.storageScore >= 7) {
      return [
        `Unmatched storage size (${storage})`,
      ]
    }
  },

  ({ laptop }) =>
    getScreen(laptop).includes('oled')
      ? ['OLED Panel']
      : [],

  (() => {
    const refreshRate = /(\d{3,})hz/

    return ({ laptop }) => {
      const hz = (getScreen(laptop).match(refreshRate) || [])[1]

      if (hz) {
        return [
          `High refresh rate display (${hz}Hz)`,
        ]
      }
    }
  })(),

  ({ laptop }) => {
    if (laptop.hasNumpad) {
      return [
        'Numpad',
      ]
    }
  },

  ({ laptop }) => {
    if (laptop.hasBacklight) {
      return [
        'Backlit keyboard',
      ]
    }
  },
]

const allCons = [
  basicCons('cpuCinebench', specLabel('cpu', 'processor')),
  basicCons('gpuMark11', specLabel('gpu', 'graphics card')),
  basicCons('memoryAmount', specLabel('memoryAmount', 'memory amount', 'GB')),
  basicCons('batteryMinutes', specLabel('batteryMinutes', 'battery life', 'min')),
  weightCons,

  ({ laptop }) => {
    if (laptop.ssdAmount === 0) {
      return [
        'No SSD',
        'No Solid State Drive',
      ]
    }

    if (laptop.ssdAmount < 250) {
      return [
        'Small SSD',
      ]
    }
  },

  ({ laptop }) => {
    const screen = getScreen(laptop)
    const hasIPS = screen.includes('ips')
    const hasOLED = screen.includes('oled')

    if (!hasIPS && !hasOLED) {
      return [
        'inferior color reproduction',
        'limited viewing angles',
        'subpar viewing angles',
        'worse contrast',
        'worse viewing angles',
      ].map(con => `No IPS Panel (${con})`)
    }
  },

  ({ laptop, laptopEvaluations }) => {
    if (laptop.screenScore < 4 && laptop.price < 500) {
      return [
        'Its screen gives away its price',
      ]
    }

    return basicCons('screen', 'screenScore', 'display')({
      laptop,
      laptopEvaluations,
    })
  },
]

const getScreen = pipe(
  propOr('', 'screen'),
  toLower,
)
